import Cookies from 'js-cookie';
import {
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
} from 'amazon-cognito-identity-js';
import { Auth, Amplify } from 'aws-amplify';

import { useRootStore } from '../stores/root';
import awsExports from '../aws-exports';
import {
  getAssumedOrgIdCookie,
  getAssumedUserIdCookie,
  getRefreshTokenFromCookie,
  getTokenKeyFromCookie,
  getTokenUsernameFromCookie,
  // refreshTokenKey,
  // tokenKeyKey,
  // tokenUsernameKey,
  unsetAuthTokenCookies,
} from './token_extraction';
import { isUndefinedOrNull, isString } from './type_guards';
import { getCookieDomain, getSecureCookie } from './cookie_handler';
import { stopAssumingUser } from './assumeUser';

// This function will attempt to retrieve the user's tokens from the cookies
// set them in local storage and then run a function to get new refresh and id
// tokens.
export async function v2AuthLoader() {
  const shouldLogOut = isV2LogoutCookieSet();
  const shouldStopAssumingUser = isV2StopAssumeUserCookieSet();

  if (shouldLogOut) {
    // console.log('logging out');
    Auth.configure(awsExports);

    await Auth.signOut();
    // Log out, do nothing more
    return;
  }

  // get the assumedOrgId
  const assumedOrgId = getAssumedOrgIdCookie();
  if (assumedOrgId) {
    const cleaned = assumedOrgId.replace('org-', '');
    // console.log('setting assumedOrgId', cleaned);
    localStorage.setItem('assumedOrgId', `org-${cleaned}`);
    localStorage.setItem('multiOrgUserSelectedOrgId', `org-${cleaned}`);
  }

  // get the assumedUserId
  const assumedUserID = getAssumedUserIdCookie();
  if (shouldStopAssumingUser) {
    // console.log('stopping assumed user');
    stopAssumingUser({ setCookie: false });
  } else if (assumedUserID) {
    // console.log('setting assumedUserID', assumedUserID);
    localStorage.setItem('assumedUserID', assumedUserID);
  }

  unsetAssumedUserOrgCookies();

  try {
    // If the user is already logged in, we stop the rest
    Amplify.configure(awsExports);
    Auth.configure(awsExports);

    await Auth.currentSession();

    unsetAuthTokenCookies();
    unsetV3LogoutCookie();
    return;
  } catch (e) {
    console.error(e);
  }

  try {
    // Get all tokens from the cookies
    const tokenUsername = getTokenUsernameFromCookie();
    const tokenKey = getTokenKeyFromCookie();
    const refreshToken = getRefreshTokenFromCookie();

    if (
      !isString(tokenUsername) ||
      !isString(tokenKey) ||
      !isString(refreshToken)
    ) {
      // console.log('invalid cookie');
      unsetAuthTokenCookies();
      return;
    }

    // Construct and use Cognito-based objects to refresh the session
    const poolData = {
      UserPoolId: import.meta.env.VITE_APP_USER_POOL_ID,
      ClientId: import.meta.env.VITE_APP_USER_POOL_CLIENT_ID,
    };
    const userPool = new CognitoUserPool(poolData);

    const userData = {
      Username: tokenUsername,
      Pool: userPool,
    };
    const user = new CognitoUser(userData);

    const token = new CognitoRefreshToken({
      RefreshToken: refreshToken,
    });

    // We refresh the session and make our own promise.
    await new Promise<void>((res, rej) => {
      user.refreshSession(token, (err, _session) => {
        if (err) {
          console.error(err);
          rej(err);
          return;
        }

        // Set the token in local storage
        localStorage.setItem(tokenKey, refreshToken);

        // Then resolve
        res();
      });
    });

    // Once we have the new tokens, we can hydrate the store
    const store = useRootStore();
    await store.hydrate();

    unsetAuthTokenCookies();
    unsetV3LogoutCookie();

    // refresh the page
    window.location.reload();
  } catch (e) {
    console.error(e);
  }
}

export function unsetAssumedUserOrgCookies() {
  Cookies.remove('assumedOrgId', { domain: '.myqsrsoft.com' });
  Cookies.remove('assumedOrgID', { domain: '.myqsrsoft.com' });
  Cookies.remove('assumedUserID', { domain: '.myqsrsoft.com' });

  Cookies.remove('assumedOrgId', { domain: 'localhost' });
  Cookies.remove('assumedOrgID', { domain: 'localhost' });
  Cookies.remove('assumedUserID', { domain: 'localhost' });
}

// #region Logout Cookie Logic
/**
 * Below is the logout cookie logic. Cookies are named after the SENDER
 * of the cookie. e.g. MyQ v3 will set a cookie starting with 'v3'.
 * The app will look out for v2 cookies sent from MyQ v2 and act upon
 * those cookies. The purpose is to log the user out of this app when
 * they log out of the other app or to signal the v2 app that the user
 * has logged out of the v3 app.
 */
const v2LogoutCookieTitle = 'v2Logout';
const v3LogoutCookieTitle = 'v3Logout';

// Checks if the V2 logout cookie message was set. It doesn't matter what the value is
export function isV2LogoutCookieSet() {
  const logoutCookie = Cookies.get(v2LogoutCookieTitle);

  unsetV2LogoutCookie();

  return !isUndefinedOrNull(logoutCookie);
}

// Unsets the v2 logout cookie. This prevents us from logging out later when the
// user logs back in.
export function unsetV2LogoutCookie() {
  const domain =
    window.location.hostname === 'localhost' ? 'localhost' : '.myqsrsoft.com';
  Cookies.remove(v2LogoutCookieTitle, { domain });
}

// Sets the v3 logout cookie to let the v2 app know that they have logge dout
export function setV3LogoutCookie() {
  Cookies.set(v3LogoutCookieTitle, 'true', {
    domain: getCookieDomain(),
    secure: getSecureCookie(),
  });
}

// Unsets the V3 logout cookie for when the user logs back in
export function unsetV3LogoutCookie() {
  const domain =
    window.location.hostname === 'localhost' ? 'localhost' : '.myqsrsoft.com';
  Cookies.remove(v3LogoutCookieTitle, { domain });
}

// #endregion

// #region Assume User Stop Cookie Logic
/**
 * Below is the stop assuming user cookie logic. Cookies are named after the
 * SENDER of the cookie. e.g. MyQ v3 will set a cookie starting with 'v3'.
 * The app will look out for v2 cookies sent from MyQ v2 and act upon
 * those cookies. The purpose is to stop assumgin user when the user has stopped
 * assuming in the other app.
 */
const v2AssumeUserStopCookieTitle = 'v2AssumeUserStop';
const v3AssumeUserStopCookieTitle = 'v3AssumeUserStop';

/**
 * Checks for a stop assume user cookie. If the cookie is set, we stop assuming
 * the user. We should also receive an Org ID of the current user in the cookie.
 * We will use this to change client to the proper org.
 */
export function isV2StopAssumeUserCookieSet(): boolean {
  const stopAssumeUserCookie = Cookies.get(v2AssumeUserStopCookieTitle);

  unsetV2StopAssumeUserCookie();

  return !isUndefinedOrNull(stopAssumeUserCookie);
}

export function unsetV2StopAssumeUserCookie() {
  const domain =
    window.location.hostname === 'localhost' ? 'localhost' : '.myqsrsoft.com';
  Cookies.remove(v2AssumeUserStopCookieTitle, { domain });
}

export function setV3StopAssumeUserCookie() {
  Cookies.set(v3AssumeUserStopCookieTitle, 'true', {
    domain: getCookieDomain(),
    secure: getSecureCookie(),
  });
}

export function unsetV3StopAssumeUserCookie() {
  const domain =
    window.location.hostname === 'localhost' ? 'localhost' : '.myqsrsoft.com';
  Cookies.remove(v3AssumeUserStopCookieTitle, { domain });
}
// #endregion
